import { useCallback, useEffect, useRef, useState } from "react";
import { useSites } from "@/contexts/SiteContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Swords,
  Loader2,
  Sparkles,
  RefreshCw,
  ShieldCheck,
  FileText,
  MapPin,
  Target,
  Flag,
  Plus,
  Users,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CompetitorHistoryPanel } from "@/components/CompetitorHistoryPanel";
import { CompetitorWatchPanel } from "@/components/CompetitorWatchPanel";

const IMPACT_STYLE = {
  "élevé": "bg-red-100 text-red-700",
  moyen: "bg-amber-100 text-amber-700",
  faible: "bg-slate-100 text-slate-600",
};
const TYPE_LABEL = {
  article: "Article",
  page_locale: "Page locale",
  faq: "FAQ",
  service_description: "Page service",
};

export default function Competitors() {
  const { activeSite } = useSites();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [generating, setGenerating] = useState(new Set());
  const [profileComp, setProfileComp] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [addName, setAddName] = useState("");
  const [addDomain, setAddDomain] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(null);
  const pollRef = useRef(null);

  const loadProfile = useCallback(async () => {
    if (!activeSite) return;
    try {
      const { data } = await api.get(`/sites/${activeSite.id}/business-profile`);
      const comps = (data?.profile?.competitors || []).filter((c) => c.name);
      setProfileComp(comps);
      setSelected(new Set(comps.map((c) => c.name)));
    } catch {
      setProfileComp([]);
      setSelected(new Set());
    }
  }, [activeSite]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const toggleCompetitor = (name) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(name)) n.delete(name); else n.add(name);
      return n;
    });

  const addCompetitor = () => {
    const name = addName.trim();
    if (!name) { toast.error("Indiquez au moins le nom du concurrent."); return; }
    if (profileComp.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      toast.warning("Ce concurrent est déjà dans la liste.");
      return;
    }
    setProfileComp((p) => [...p, { name, domain: addDomain.trim(), _new: true }]);
    setSelected((prev) => new Set(prev).add(name));
    setAddName("");
    setAddDomain("");
    toast.success(`« ${name} » ajouté — il sera enregistré dans votre profil au lancement de l'analyse.`);
  };

  const removeCompetitor = async (c, i) => {
    const key = `${c.name}-${i}`;
    if (confirmRemove !== key) {
      setConfirmRemove(key);
      setTimeout(() => setConfirmRemove((k) => (k === key ? null : k)), 4000);
      return;
    }
    setConfirmRemove(null);
    if (c._new) {
      setProfileComp((p) => p.filter((x) => x.name !== c.name));
      setSelected((prev) => { const n = new Set(prev); n.delete(c.name); return n; });
      return;
    }
    try {
      await api.post(`/sites/${activeSite.id}/competitors/remove`, { name: c.name });
      toast.success(`« ${c.name} » retiré de votre profil business.`);
      loadProfile();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible.");
    }
  };

  const loadLatest = useCallback(async () => {
    if (!activeSite) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/sites/${activeSite.id}/competitor-analysis/latest`);
      setReport(data && data.summary ? data : null);
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [activeSite]);

  useEffect(() => {
    loadLatest();
    return () => clearInterval(pollRef.current);
  }, [loadLatest]);

  const launch = async () => {
    if (!activeSite) return;
    const chosen = profileComp
      .filter((c) => selected.has(c.name))
      .map(({ name, domain }) => ({ name, domain: domain || "" }));
    if (chosen.length === 0) {
      toast.error("Sélectionnez au moins un concurrent (ou ajoutez-en un).");
      return;
    }
    setRunning(true);
    try {
      const { data } = await api.post(`/sites/${activeSite.id}/competitor-analysis`, { competitors: chosen });
      toast.info(chosen.length === 1
        ? `Duel lancé contre ${chosen[0].name} — analyse approfondie en cours…`
        : `Analyse lancée sur ${chosen.length} concurrents…`);
      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts += 1;
        if (attempts > 90) {
          clearInterval(pollRef.current);
          setRunning(false);
          toast.error("L'analyse prend trop de temps. Réessayez.");
          return;
        }
        try {
          const { data: job } = await api.get(`/content/jobs/${data.job_id}`);
          if (job.status === "completed") {
            clearInterval(pollRef.current);
            setReport(job.result);
            setRunning(false);
            loadProfile();
            toast.success(job.result?.duel_mode ? "Duel terminé — rapport approfondi prêt !" : "Plan de bataille prêt !");
          } else if (job.status === "failed") {
            clearInterval(pollRef.current);
            setRunning(false);
            toast.error(job.error || "L'analyse a échoué.");
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (e) {
      setRunning(false);
      toast.error(e?.response?.data?.detail || "Impossible de lancer l'analyse.");
    }
  };

  const generateContent = async (gap, genKey) => {
    setGenerating((s) => new Set(s).add(genKey));
    try {
      const { data } = await api.post("/content/generate-async", {
        site_id: activeSite.id,
        content_type: gap.type || "article",
        topic: gap.title,
        keywords: gap.target_keywords || [],
        city: gap.city || null,
        tone: "professionnel",
        target_length: "moyen",
      });
      toast.info("Génération lancée — le brouillon arrive dans « Brouillons » (1-2 min).");
      const interval = setInterval(async () => {
        try {
          const { data: job } = await api.get(`/content/jobs/${data.job_id}`);
          if (job.status === "completed") {
            clearInterval(interval);
            setGenerating((s) => { const n = new Set(s); n.delete(genKey); return n; });
            toast.success(`Brouillon « ${job.result?.title || ""} » prêt !`);
          } else if (job.status === "failed") {
            clearInterval(interval);
            setGenerating((s) => { const n = new Set(s); n.delete(genKey); return n; });
            toast.error(job.error || "Génération échouée");
          }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) {
      setGenerating((s) => { const n = new Set(s); n.delete(genKey); return n; });
      toast.error(e?.response?.data?.detail || "Impossible de lancer la génération.");
    }
  };

  if (!activeSite) {
    return (
      <div className="p-8">
        <div className="text-slate-600" data-testid="competitors-no-site">Connectez d'abord un site pour lancer l'analyse.</div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl" data-testid="competitors-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-9 h-9 rounded-md bg-[#002FA7] flex items-center justify-center">
              <Swords className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-950">Analyse concurrentielle</h1>
          </div>
          <p className="text-sm text-slate-600 max-w-2xl">
            L'IA compare votre site à vos vrais concurrents (définis dans le Business Analyzer) :
            mots-clés qu'ils dominent, contenus qui vous manquent, et plan de bataille priorisé.
          </p>
        </div>
        <Button
          onClick={launch}
          disabled={running}
          data-testid="competitors-launch-button"
          className="bg-[#002FA7] hover:bg-[#00248A] text-white"
        >
          {running ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse en cours…</>
          ) : selected.size === 1 ? (
            <><Swords className="w-4 h-4 mr-2" /> Lancer le duel</>
          ) : (
            <><Sparkles className="w-4 h-4 mr-2" /> Analyser ({selected.size})</>
          )}
        </Button>
      </div>

      {/* Sélection des concurrents */}
      <div className="border border-slate-200 bg-white rounded-lg p-5 mb-6" data-testid="competitor-select-panel">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Users className="w-4 h-4 text-[#002FA7]" /> Concurrents à analyser
            <span className="text-xs font-normal text-slate-500">({selected.size} sélectionné{selected.size > 1 ? "s" : ""})</span>
          </div>
          <div className="flex items-center gap-3">
            {profileComp.length > 1 && (
              <button
                onClick={() =>
                  setSelected(selected.size === profileComp.length ? new Set() : new Set(profileComp.map((c) => c.name)))
                }
                data-testid="select-all-competitors-toggle"
                className="text-[11px] font-medium text-[#002FA7] hover:underline"
              >
                {selected.size === profileComp.length ? "Aucun" : "Tout sélectionner"}
              </button>
            )}
          {selected.size === 1 && (
            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200" data-testid="duel-badge">
              ⚔️ Mode duel approfondi — comparaison page par page
            </span>
          )}
          </div>
        </div>
        {profileComp.length === 0 ? (
          <p className="text-sm text-slate-500 mb-3">
            Aucun concurrent dans votre profil business — lancez le Business Analyzer, ou ajoutez-en un ci-dessous.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2 mb-4">
            {profileComp.map((c, i) => (
              <span
                key={`${c.name}-${i}`}
                onClick={() => toggleCompetitor(c.name)}
                data-testid={`competitor-checkbox-${i}`}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                  selected.has(c.name)
                    ? "bg-[#002FA7] text-white border-[#002FA7]"
                    : "bg-white text-slate-600 border-slate-300 hover:border-[#002FA7]/50"
                }`}
              >
                {selected.has(c.name) ? "✓" : "○"} {c.name}
                {c.domain && <span className={selected.has(c.name) ? "text-blue-200" : "text-slate-400"}>· {c.domain}</span>}
                {c._new && <span className="text-[9px] uppercase font-bold">nouveau</span>}
                <button
                  onClick={(e) => { e.stopPropagation(); removeCompetitor(c, i); }}
                  data-testid={`remove-competitor-${i}`}
                  title={confirmRemove === `${c.name}-${i}` ? "Cliquez à nouveau pour confirmer la suppression" : "Retirer ce concurrent du profil"}
                  className={`ml-0.5 rounded-full p-0.5 transition-colors ${
                    confirmRemove === `${c.name}-${i}`
                      ? "bg-red-600 text-white"
                      : selected.has(c.name)
                        ? "text-blue-200 hover:text-white hover:bg-white/20"
                        : "text-slate-400 hover:text-red-600 hover:bg-red-50"
                  }`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
          <input
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCompetitor()}
            placeholder="Nom du concurrent"
            data-testid="add-competitor-name-input"
            className="border border-slate-300 rounded-md px-3 py-1.5 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7]"
          />
          <input
            value={addDomain}
            onChange={(e) => setAddDomain(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCompetitor()}
            placeholder="Site web (ex: concurrent.ch)"
            data-testid="add-competitor-domain-input"
            className="border border-slate-300 rounded-md px-3 py-1.5 text-sm w-52 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7]"
          />
          <button
            onClick={addCompetitor}
            data-testid="add-competitor-button"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#002FA7] border border-[#002FA7]/30 rounded-md px-3 py-1.5 hover:bg-blue-50"
          >
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </button>
          <span className="text-[11px] text-slate-500">Les nouveaux concurrents sont enregistrés dans votre profil business.</span>
        </div>
      </div>

      <CompetitorWatchPanel siteId={activeSite.id} />

      <CompetitorHistoryPanel
        siteId={activeSite.id}
        refreshKey={report?.id}
        currentReportId={report?.id}
        onViewReport={setReport}
      />

      {running && (
        <div className="border border-[#002FA7]/20 bg-blue-50/50 rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="competitors-running-banner">
          <Loader2 className="w-5 h-5 text-[#002FA7] animate-spin flex-shrink-0" />
          <div className="text-sm text-slate-700">
            <span className="font-semibold">Analyse en cours (1-3 min)</span> — crawl de votre site et des concurrents,
            puis construction du plan de bataille.
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Chargement…
        </div>
      ) : !report ? (
        !running && (
          <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center" data-testid="competitors-empty-state">
            <Swords className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <div className="font-semibold text-slate-900 mb-1">Aucune analyse pour {activeSite.name}</div>
            <div className="text-sm text-slate-500">
              Prérequis : des concurrents définis dans le Business Analyzer (l'IA les propose, vous pouvez les corriger).
            </div>
          </div>
        )
      ) : (
        <div className="space-y-6">
          {/* Summary */}
          <div className="border border-[#002FA7]/20 bg-blue-50/40 rounded-lg p-5" data-testid="competitors-summary">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Flag className="w-4 h-4 text-[#002FA7]" />
                  <div className="text-sm font-semibold text-slate-900">
                    {report.duel_mode ? `⚔️ Duel : ${activeSite.name} vs ${(report.analyzed_competitors || [])[0] || "concurrent"}` : "Paysage concurrentiel"}
                  </div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{report.summary}</p>
                <div className="mt-2 text-[11px] text-slate-400">
                  Analyse du {new Date(report.created_at).toLocaleString("fr-FR")}
                  {report.analyzed_competitors?.length ? ` — concurrents analysés : ${report.analyzed_competitors.join(", ")}` : ""}
                  {report.competitors_crawled?.length ? ` — sites crawlés : ${report.competitors_crawled.join(", ")}` : ""}
                </div>
              </div>
              {report.duel_mode && typeof report.victory_score === "number" && (
                <div className="flex flex-col items-center flex-shrink-0 border border-slate-200 bg-white rounded-lg px-4 py-2.5" data-testid="duel-victory-score">
                  <div className={`text-3xl font-display font-bold leading-none ${
                    report.victory_score >= 60 ? "text-green-600" : report.victory_score >= 40 ? "text-amber-600" : "text-red-600"
                  }`}>
                    {report.victory_score}
                  </div>
                  <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 mt-1">Score de victoire</div>
                  <div className="text-[10px] text-slate-500">{report.victory_score >= 50 ? "Vous dominez" : "Concurrent devant"}</div>
                </div>
              )}
            </div>
          </div>

          {/* Your advantages */}
          <div className="border border-green-200 bg-green-50/50 rounded-lg p-5" data-testid="competitors-advantages">
            <div className="flex items-center gap-2 font-semibold text-green-800 text-sm mb-3">
              <ShieldCheck className="w-4 h-4" /> Vos avantages face à eux
            </div>
            <ul className="space-y-2">
              {(report.your_advantages || []).map((a, i) => (
                <li key={i} className="text-sm text-slate-700 flex gap-2">
                  <span className="text-green-600 flex-shrink-0">✓</span> {a}
                </li>
              ))}
            </ul>
          </div>

          {/* Per-competitor */}
          <div>
            <h2 className="text-base font-display font-bold text-slate-950 mb-3">Concurrent par concurrent</h2>
            <div className="space-y-3" data-testid="competitors-list">
              {(report.competitors || []).map((c, i) => (
                <div key={i} className="border border-slate-200 bg-white rounded-lg p-4" data-testid={`competitor-card-${i}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <div className="font-semibold text-sm text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-500 italic">{c.positioning}</div>
                  </div>
                  {(c.keywords_they_dominate || []).length > 0 && (
                    <div className="mb-2">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Mots-clés qu'il domine</div>
                      <div className="flex flex-wrap gap-1.5">
                        {c.keywords_they_dominate.map((k, ki) => (
                          <span key={ki} title={k.why} className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium cursor-help">
                            {k.keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs mt-2">
                    <div><span className="font-semibold text-slate-700">Forces :</span> <span className="text-slate-600">{c.their_strengths}</span></div>
                    <div><span className="font-semibold text-slate-700">Faiblesses :</span> <span className="text-slate-600">{c.their_weaknesses}</span></div>
                    <div><span className="font-semibold text-green-700">Comment le battre :</span> <span className="text-slate-600">{c.how_to_beat}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Duel : comparaison page par page */}
          {report.duel_mode && (report.page_comparison || []).length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Swords className="w-4 h-4 text-red-600" />
                <h2 className="text-base font-display font-bold text-slate-950">Duel page par page</h2>
              </div>
              <div className="space-y-3" data-testid="competitors-page-comparison">
                {report.page_comparison.map((p, i) => (
                  <div key={i} className="border border-slate-200 bg-white rounded-lg p-4" data-testid={`page-comparison-${i}`}>
                    <div className="font-semibold text-sm text-slate-900 mb-2">{p.topic}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs mb-2">
                      <div className="border border-red-100 bg-red-50/40 rounded-md p-2.5">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-red-600 mb-1">Eux</div>
                        <span className="text-slate-700">{p.their_page}</span>
                      </div>
                      <div className={`border rounded-md p-2.5 ${(p.our_page || "").toUpperCase().includes("MANQUANTE") ? "border-amber-200 bg-amber-50/50" : "border-blue-100 bg-blue-50/40"}`}>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#002FA7] mb-1">Vous</div>
                        <span className="text-slate-700">{p.our_page}</span>
                      </div>
                    </div>
                    <div className="text-xs"><span className="font-semibold text-slate-700">Verdict :</span> <span className="text-slate-600">{p.verdict}</span></div>
                    {p.action && <div className="text-xs mt-1"><span className="font-semibold text-green-700">Action :</span> <span className="text-slate-600">{p.action}</span></div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content gaps */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-[#002FA7]" />
              <h2 className="text-base font-display font-bold text-slate-950">Contenus qu'ils ont — et pas vous</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="competitors-gaps">
              {(report.content_gaps || []).map((g, i) => {
                const genKey = `gap-${i}`;
                return (
                  <div key={i} className="border border-slate-200 bg-white rounded-lg p-4 flex flex-col">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <Badge variant="outline" className="text-[9px] border-[#002FA7] text-[#002FA7]">{TYPE_LABEL[g.type] || g.type}</Badge>
                      {g.inspired_by && <span className="text-[10px] text-red-600 font-medium">chez {g.inspired_by}</span>}
                      {g.city && <span className="text-[10px] text-slate-500 flex items-center gap-0.5"><MapPin className="w-3 h-3" />{g.city}</span>}
                    </div>
                    <div className="font-semibold text-sm text-slate-900 mb-1">{g.title}</div>
                    <p className="text-xs text-slate-600 flex-1">{g.why}</p>
                    <div className="mt-2 text-[11px] text-slate-500">Cibles : {(g.target_keywords || []).join(", ")}</div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => generateContent(g, genKey)}
                      disabled={generating.has(genKey)}
                      data-testid={`competitors-generate-gap-${i}`}
                      className="mt-3 border-[#002FA7] text-[#002FA7] hover:bg-blue-50 w-fit"
                    >
                      {generating.has(genKey) ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Génération…</>
                      ) : (
                        <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Combler ce gap</>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Battle plan */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-[#002FA7]" />
              <h2 className="text-base font-display font-bold text-slate-950">Plan de bataille</h2>
            </div>
            <div className="space-y-3" data-testid="competitors-battle-plan">
              {(report.battle_plan || []).map((a, i) => (
                <div key={i} className="border border-slate-200 bg-white rounded-lg p-4 flex items-start gap-3">
                  <div className="text-lg font-display font-bold text-[#002FA7] w-6 flex-shrink-0">{i + 1}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <div className="font-semibold text-sm text-slate-900">{a.action}</div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${IMPACT_STYLE[a.impact] || IMPACT_STYLE.faible}`}>
                        Impact {a.impact}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-slate-100 text-slate-600">Effort {a.effort}</span>
                      {a.timeframe && <span className="text-[10px] text-slate-500">{a.timeframe}</span>}
                    </div>
                    <p className="text-xs text-slate-600">{a.details}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
